import { useCallback, useMemo, useState, useEffect, useRef } from "react";
import { Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import {
  GoogleMap,
  useLoadScript,
  InfoWindow,
  HeatmapLayer,
  Libraries,
} from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Property } from "../../../types/search";
import { useTenant } from "../../../contexts/TenantContext";
import { getOptimizedImageUrl } from "../../../utils/imageUtils";

interface PriceRange {
  min: number;
  max: number;
}

const DEFAULT_PRICE_RANGES: PriceRange[] = [
  { min: 0, max: 500000 },
  { min: 500000, max: 1000000 },
  { min: 1000000, max: 2000000 },
  { min: 2000000, max: Infinity },
];

const GOOGLE_MAPS_LIBRARIES: Libraries = ["places", "visualization"];

const getMarkerColor = (price: number): string => {
  if (price < 500000) return "#34D399"; // green
  if (price < 1000000) return "#3B82F6"; // blue
  if (price < 2000000) return "#F59E0B"; // yellow
  return "#EF4444"; // red
};

const formatPrice = (price: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(price);
};

interface MapViewProps {
  properties: Property[];
  onPropertySelect?: (property: Property) => void;
  defaultLocation?: string;
  onMapDragEnd?: (center: { lat: number; lng: number }) => void;
  isLoading?: boolean;
  mlsLogosMap?: Map<string, string>;
}

const MapView = ({
  properties,
  onPropertySelect,
  defaultLocation,
  onMapDragEnd,
  isLoading = false,
  mlsLogosMap,
}: MapViewProps) => {
  const { config } = useTenant();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(
    null
  );
  const [defaultCenter, setDefaultCenter] = useState({ lat: 0, lng: 0 });
  const [userDraggedCenter, setUserDraggedCenter] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const [mapTypeId, setMapTypeId] = useState<string>("roadmap");
  const [priceFilter, setPriceFilter] = useState<PriceRange | null>(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [controlsMinimized, setControlsMinimized] = useState(false);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [clusterer, setClusterer] = useState<MarkerClusterer | null>(null);
  const hasAutoClosedRef = useRef(false);

  if (!config) return null;

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  // Debounce function to prevent too many API calls when zooming
  const debounceTimeout = useRef<number | null>(null);

  const debounce = useCallback((func: () => void, wait: number) => {
    if (debounceTimeout.current) {
      window.clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = window.setTimeout(() => {
      func();
      debounceTimeout.current = null;
    }, wait);
  }, []);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      setMap(map);

      // Auto-minimize controls after 3 seconds on initial load only
      if (!hasAutoClosedRef.current) {
        hasAutoClosedRef.current = true;
        setTimeout(() => {
          setControlsMinimized(true);
        }, 3000);
      }

      // Add listener for dragend event
      if (map && onMapDragEnd) {
        // Function to handle center change and trigger search
        const handleCenterChange = () => {
          const newCenter = map.getCenter();
          if (newCenter) {
            const centerCoords = {
              lat: newCenter.lat(),
              lng: newCenter.lng(),
            };
            setUserDraggedCenter(centerCoords);
            onMapDragEnd(centerCoords);
          }
        };

        // Add listeners for both drag and zoom events
        map.addListener("dragend", handleCenterChange);

        // Debounce the zoom_changed event to prevent too many API calls
        map.addListener("zoom_changed", () => {
          debounce(handleCenterChange, 500);
        });
      }
    },
    [onMapDragEnd, debounce]
  );

  const handleMarkerClick = useCallback((property: Property) => {
    setSelectedProperty(property);
  }, []);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedProperty(null);
  }, []);

  const handlePropertySelect = useCallback(
    (property: Property) => {
      onPropertySelect?.(property);
      setSelectedProperty(null);
    },
    [onPropertySelect]
  );

  const handleMapTypeChange = useCallback((mapType: string) => {
    setMapTypeId(mapType);
  }, []);

  // Filter properties based on price range
  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      if (!priceFilter) return true;
      return (
        property.price >= priceFilter.min && property.price <= priceFilter.max
      );
    });
  }, [properties, priceFilter]);

  // Keep track of previous properties to avoid clearing markers during loading
  const prevPropertiesRef = useRef<Property[]>([]);

  // Setup marker clusterer
  useEffect(() => {
    if (map && isLoaded && !isLoading) {
      if (clusterer) {
        clusterer.clearMarkers();
      }

      // Update the reference even if empty
      prevPropertiesRef.current = filteredProperties;

      // Only create new markers if we have properties to show
      if (filteredProperties.length > 0) {
        // Filter properties that can be shown on map (have coordinates and showMapLink is not false)
        const propertiesWithCoords = filteredProperties.filter(
          (property) =>
            property.address?.coordinates &&
            // Only include properties that don't have showMapLink explicitly set to false
            property.displayRules?.showMapLink !== false
        );

        const markers = propertiesWithCoords.map((property) => {
          const marker = new google.maps.Marker({
            position: property.address.coordinates!,
            title: `${property.address.full} - ${formatPrice(property.price)}`,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: getMarkerColor(property.price),
              fillOpacity: 0.7,
              strokeColor: "#FFFFFF",
              strokeWeight: 2,
            },
          });

          marker.addListener("click", () => handleMarkerClick(property));
          return marker;
        });

        const newClusterer = new MarkerClusterer({
          map,
          markers,
        });
        setClusterer(newClusterer);
      } else {
        // If no properties to show, set clusterer to null
        console.log("No properties to show, clearing clusterer");
        setClusterer(null);
      }

      return () => {
        if (clusterer && !isLoading) {
          clusterer.clearMarkers();
        }
      };
    }
  }, [map, filteredProperties, handleMarkerClick, isLoaded, isLoading]);

  // Commented out heatmap functionality
  /*
  // Generate heatmap data
  const heatmapData = useMemo(() => {
    if (!isLoaded) return [];

    // If loading and we have previous properties, use those for the heatmap
    const propertiesToUse =
      isLoading && prevPropertiesRef.current.length > 0
        ? prevPropertiesRef.current.filter(
            (p) =>
              !priceFilter ||
              (p.price >= priceFilter.min && p.price <= priceFilter.max)
          )
        : filteredProperties;

    return propertiesToUse
      .filter((p) => p.address?.coordinates)
      .map((p) => ({
        location: new google.maps.LatLng(
          p.address.coordinates!.lat,
          p.address.coordinates!.lng
        ),
        weight: p.price / 1000000, // Normalize price for heatmap intensity
      }));
  }, [
    filteredProperties,
    isLoaded,
    isLoading,
    prevPropertiesRef.current,
    priceFilter,
  ]);
  */

  // Get coordinates from default location
  useEffect(() => {
    if (!isLoaded) return;

    if (
      defaultLocation &&
      (!properties.length || !properties[0]?.address?.coordinates)
    ) {
      try {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ address: defaultLocation }, (results, status) => {
          if (status === "OK" && results?.[0]?.geometry?.location) {
            const location = results[0].geometry.location;
            setDefaultCenter({
              lat: location.lat(),
              lng: location.lng(),
            });
          }
        });
      } catch (error) {
        console.error("Error geocoding default location:", error);
      }
    }
  }, [isLoaded, defaultLocation, properties]);

  const center = useMemo(() => {
    // If user has dragged the map, use that center
    if (userDraggedCenter) {
      return userDraggedCenter;
    }

    // Otherwise, if we have properties with coordinates, use their average
    const coords = properties
      .filter((p) => p.address?.coordinates)
      .map((p) => p.address.coordinates!);

    if (coords.length > 0) {
      const total = coords.reduce(
        (acc, curr) => ({
          lat: acc.lat + curr.lat,
          lng: acc.lng + curr.lng,
        }),
        { lat: 0, lng: 0 }
      );

      return {
        lat: total.lat / coords.length,
        lng: total.lng / coords.length,
      };
    }

    // If no property coordinates, use the geocoded default location
    return defaultCenter;
  }, [properties, defaultCenter, userDraggedCenter]);

  if (!isLoaded) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        Loading map...
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {/* Loading indicator with Lucide icon */}
      {isLoading && (
        <div className="absolute top-4 left-4 z-10 bg-white rounded-lg shadow-lg p-3 flex items-center space-x-2">
          <Loader2
            className="animate-spin h-5 w-5"
            style={{ color: config.branding.colors.primary }}
          />
          <span className="text-sm font-medium text-gray-700">
            Searching...
          </span>
        </div>
      )}
      {isDesktop && (
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-lg max-w-xs w-full">
          {/* Map Controls Header */}
          <div
            className={`flex items-center justify-between p-4 py-2 cursor-pointer hover:bg-gray-50 transition-colors rounded-t-lg ${
              !controlsMinimized ? "border-b" : "rounded-b-lg"
            }`}
            onClick={() => setControlsMinimized(!controlsMinimized)}
          >
            <div className="text-sm font-semibold text-gray-700">
              Map Controls
            </div>
            <div
              className="p-1 hover:bg-gray-200 rounded transition-all duration-200"
              aria-label={
                controlsMinimized ? "Expand controls" : "Minimize controls"
              }
            >
              {controlsMinimized ? (
                <ChevronDown className="h-4 w-4 text-gray-600 transition-transform duration-200" />
              ) : (
                <ChevronUp className="h-4 w-4 text-gray-600 transition-transform duration-200" />
              )}
            </div>
          </div>

          {/* Collapsible Controls Content */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              controlsMinimized
                ? "max-h-0 opacity-0"
                : "max-h-[500px] opacity-100"
            }`}
          >
            <div className="p-4 space-y-4">
              {/* Map Type Controls */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500">
                  View Type
                </div>
                <div className="flex gap-2">
                  <button
                    className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 ${
                      mapTypeId === "roadmap"
                        ? "bg-primary text-white shadow-sm"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    style={{
                      backgroundColor:
                        mapTypeId === "roadmap"
                          ? config.branding.colors.primary
                          : undefined,
                    }}
                    onClick={() => handleMapTypeChange("roadmap")}
                  >
                    Map
                  </button>
                  <button
                    className={`flex-1 px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 ${
                      mapTypeId === "satellite"
                        ? "bg-primary text-white shadow-sm"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                    style={{
                      backgroundColor:
                        mapTypeId === "satellite"
                          ? config.branding.colors.primary
                          : undefined,
                    }}
                    onClick={() => handleMapTypeChange("satellite")}
                  >
                    Satellite
                  </button>
                </div>
              </div>

              {/* Price Range Filter */}
              <div className="space-y-2">
                <div className="text-xs font-medium text-gray-500">
                  Price Range
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {DEFAULT_PRICE_RANGES.map((range, index) => (
                    <button
                      key={index}
                      className={`px-2 py-1.5 text-xs font-medium rounded transition-all duration-200 ${
                        priceFilter === range
                          ? "text-white shadow-sm"
                          : "bg-gray-100 hover:bg-gray-200"
                      } flex items-center gap-1`}
                      style={{
                        backgroundColor:
                          priceFilter === range ? "#313131" : undefined,
                      }}
                      onClick={() => setPriceFilter(range)}
                    >
                      <span
                        className="inline-block w-3 h-3 rounded-full"
                        style={{
                          backgroundColor:
                            range.max === Infinity
                              ? "#EF4444" // red for highest range
                              : range.max === 2000000
                              ? "#F59E0B" // yellow for 1M-2M
                              : range.max === 1000000
                              ? "#3B82F6" // blue for 500K-1M
                              : "#34D399", // green for 0-500K
                        }}
                      ></span>
                      {range.max === Infinity
                        ? `$${range.min / 1000000}M+`
                        : `$${range.min / 1000000}M - $${range.max / 1000000}M`}
                    </button>
                  ))}
                </div>
                {priceFilter && (
                  <button
                    className="w-full text-xs font-medium !bg-transparent hover:opacity-80 transition-opacity text-gray-800"
                    onClick={() => setPriceFilter(null)}
                  >
                    Clear Filter
                  </button>
                )}
              </div>

              {/* Heatmap Toggle - Commented out
              <div className="pt-2 border-t">
                <button
                  className={`w-full px-3 py-2 text-sm font-medium rounded transition-all duration-200 ${
                    showHeatmap
                      ? "text-white shadow-sm"
                      : "bg-gray-100 hover:bg-gray-200"
                  }`}
                  style={{
                    backgroundColor: showHeatmap
                      ? config.branding.colors.primary
                      : undefined,
                  }}
                  onClick={() => setShowHeatmap(!showHeatmap)}
                >
                  {showHeatmap ? "Hide Heatmap" : "Show Price Heatmap"}
                </button>
              </div>
              */}
            </div>
          </div>
        </div>
      )}

      <GoogleMap
        zoom={12}
        center={center}
        mapContainerClassName="w-full h-full"
        options={{
          mapTypeId,
          zoomControl: isDesktop,
          streetViewControl: isDesktop,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
        onLoad={onMapLoad}
      >
        {/* Markers are now handled by the clusterer */}

        {/* Heatmap functionality commented out
        {showHeatmap && (
          <HeatmapLayer
            data={heatmapData}
            options={{
              radius: 20,
              opacity: 0.6,
              gradient: [
                "rgba(0, 255, 255, 0)",
                "rgba(0, 255, 255, 1)",
                "rgba(0, 191, 255, 1)",
                "rgba(0, 127, 255, 1)",
                "rgba(0, 63, 255, 1)",
                "rgba(0, 0, 255, 1)",
                "rgba(0, 0, 223, 1)",
                "rgba(0, 0, 191, 1)",
                "rgba(0, 0, 159, 1)",
                "rgba(0, 0, 127, 1)",
                "rgba(63, 0, 91, 1)",
                "rgba(127, 0, 63, 1)",
                "rgba(191, 0, 31, 1)",
                "rgba(255, 0, 0, 1)",
              ],
            }}
          />
        )}
        */}

        {selectedProperty &&
          selectedProperty.address?.coordinates &&
          selectedProperty.displayRules?.showMapLink !== false && (
            <InfoWindow
              position={selectedProperty.address.coordinates}
              onCloseClick={handleInfoWindowClose}
            >
              <div className="max-w-xs">
                <div className="flex flex-col gap-1">
                  {(selectedProperty.photos?.[0] ||
                    selectedProperty.images?.[0]) && (
                    <img
                      src={getOptimizedImageUrl(
                        selectedProperty.photos?.[0] ||
                          selectedProperty.images?.[0],
                        isMobile ? 300 : 400
                      )}
                      alt={selectedProperty.address.full}
                      className="w-full h-32 object-cover rounded mb-3"
                      loading="lazy"
                      decoding="async"
                    />
                  )}
                  <h3 className="text-base font-semibold text-gray-900">
                    {formatPrice(selectedProperty.price)}
                  </h3>
                  <p className="text-sm font-medium  text-gray-600">
                    {selectedProperty.address.full}
                  </p>
                  <div className="flex gap-4 text-sm text-gray-600">
                    {selectedProperty.bedrooms > 0 && (
                      <span>{selectedProperty.bedrooms} beds</span>
                    )}
                    {selectedProperty.bathrooms > 0 && (
                      <span>{selectedProperty.bathrooms} baths</span>
                    )}
                    {selectedProperty.squareFeet > 0 && (
                      <span>{selectedProperty.squareFeet} sqft</span>
                    )}
                  </div>

                  {/* MLS Info with Logo */}
                  <div className="mt-2 pt-2 border-t border-gray-200">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 text-xs text-gray-500">
                        <p>MLS# {selectedProperty.mlsNumber}</p>
                        <p>{selectedProperty.listingOffice?.name || "-"}</p>
                      </div>
                      {mlsLogosMap &&
                        selectedProperty.mlsBoardCode &&
                        mlsLogosMap.get(selectedProperty.mlsBoardCode) && (
                          <img
                            src={mlsLogosMap.get(selectedProperty.mlsBoardCode)}
                            alt={`${selectedProperty.mlsBoardCode} logo`}
                            className="h-4 object-contain"
                            loading="lazy"
                            decoding="async"
                          />
                        )}
                    </div>
                  </div>

                  <button
                    onClick={() => handlePropertySelect(selectedProperty)}
                    className="mt-2 w-full text-white px-4 py-2 rounded transition-all duration-200 hover:opacity-90"
                    style={{ backgroundColor: config.branding.colors.primary }}
                  >
                    View Details
                  </button>
                </div>
              </div>
            </InfoWindow>
          )}
      </GoogleMap>
    </div>
  );
};

export default MapView;
